import React, { useEffect, useState, useMemo, useCallback, forwardRef } from "react";
import { Table, Text, Button, Input, Drawer, Accordion, SliderBar } from "@ui-component-library";
import { useDebounce } from "@ui-component-library/hooks/useDebounce";
import { useMetricData } from "../hooks/UseMetricData";
import Skeleton from "../../comps/Skeleton";
import { formatNumber } from "../../utils";
import { SubscriptionCoverRow } from "../components/SubscriptionCovers";
import { useNavigationStore } from "../../store/NavigationStore";
import { DynamicModal } from "@components/DynamicModal";
import WidgetWrapper from "../../comps/WidgetWrapper";

import { FellingClassesLegend } from "../../helpers";
import CaretClosedDown from "@assets/icons/caret-closed-down.svg";
import FilterIcon from "@assets/icons/filter.svg";
import CrossIcon from "@assets/icons/cross.svg";
import SortingArrowsIcon from "@assets/icons/sorting-arrows.svg";

const FILTER_DEBOUNCE_MS = 200;

const DEFAULT_SORT = { key: "designation", order: "desc" };
const SORT_KEYS = ["avd", "designation", "hkl", "si", "age", "area", "volume"];

function getSortValue(section, key) {
  const value = section[key];
  const num = Number(value);
  return Number.isNaN(num) ? value : num;
}

/** parseFloat but treat NaN (empty/invalid) as fallback — `??` does not catch NaN. */
function parseFilterBound(val, fallback) {
  const n = Number.parseFloat(val);
  return Number.isNaN(n) ? fallback : n;
}

/** Parse SI string into number (if any) and letter part. No number => num: null. */
function parseSi(siStr) {
  const s = String(siStr ?? "").trim();
  const numMatch = s.match(/\d+/);
  const num = numMatch ? Number(numMatch[0]) : null;
  const letter = s.replace(/\d+/g, "").trim() || s;
  return { num, letter, raw: s };
}

function compareSections(a, b, key, order) {
  if (key === "designation") {
    const cmpDesignation = String(a.designation ?? "").localeCompare(
      String(b.designation ?? ""),
      undefined,
      { numeric: true }
    );
    if (cmpDesignation !== 0) {
      return order === "asc" ? -cmpDesignation : cmpDesignation;
    }
    // Secondary sort by avd: always lowest to highest
    return (Number(a.avd) || 0) - (Number(b.avd) || 0);
  }

  if (key === "si") {
    const pa = parseSi(a.si);
    const pb = parseSi(b.si);
    const aHasNum = pa.num !== null;
    const bHasNum = pb.num !== null;
    if (!aHasNum && bHasNum) return 1;
    if (aHasNum && !bHasNum) return -1;
    if (!aHasNum && !bHasNum) {
      return pa.raw.localeCompare(pb.raw, undefined, { numeric: true });
    }
    const cmpNum = order === "desc" ? pb.num - pa.num : pa.num - pb.num;
    if (cmpNum !== 0) return cmpNum;

    // Letters always A–Z
    return pa.letter.localeCompare(pb.letter, undefined, { numeric: true });
  }

  const valA = getSortValue(a, key);
  const valB = getSortValue(b, key);
  let cmp;
  if (typeof valA === "number" && typeof valB === "number") {
    cmp = valA - valB;
  } else {
    cmp = String(valA ?? "").localeCompare(String(valB ?? ""), undefined, { numeric: true });
  }
  return order === "asc" ? -cmp : cmp;
}

function sectionMatchesFilter(section, filter) {
  const area = getSortValue(section, "area");
  const age = getSortValue(section, "age");
  const volume = getSortValue(section, "volume");

  if (typeof area === "number" && (area < filter.area[0] || area > filter.area[1])) return false;
  if (typeof age === "number") {
    const inRange =
      filter.age[1] > 100
        ? age < filter.age[0]
        : age < filter.age[0] || age > filter.age[1];
    if (inRange) return false;
  }
  if (typeof volume === "number" && (volume < filter.volume[0] || volume > filter.volume[1])) return false;
  if (filter.hkl?.length > 0 && !filter.hkl.includes(section.hkl)) return false;

  if (filter.tgl?.length > 0) {
    const tgl = section.tgl;
    const entries = tgl && typeof tgl === "object" ? Object.entries(tgl) : [];
    if (entries.length === 0) return false;
    const highestTgl = entries.reduce((a, b) => (a[1] > b[1] ? a : b))[0];
    if (!filter.tgl.includes(highestTgl) || tgl[highestTgl] === 0) return false;
  }
  return true;
}

function getAgeSpan(age) {
  if (age <= 10) return "0-10";
  if (age <= 20) return "10-20";
  if (age <= 30) return "20-30";
  if (age <= 40) return "30-40";
  if (age <= 50) return "40-50";
  if (age <= 60) return "50-60";
  if (age <= 70) return "60-70";
  if (age <= 80) return "70-80";
  if (age <= 90) return "80-90";
  if (age < 100) return "90-100";
  return "100+";
}

const TableRow = React.memo(function TableRow({ section, propDfr, onOpenSection, getHklColor, view }) {
  const highlight = section.nr === view.section ? true : false;
  
  return (
    <Table.Row onClick={() => onOpenSection(section.nr)} className={highlight ? "bg-[#F0F0F0]" : ""}>
      <Table.Cell>
        <Text variant="text-sm/leading-normal/normal">#{section.avd}</Text>
      </Table.Cell>
      <Table.Cell>
        <div className="flex items-center">
          <Text variant="text-sm/leading-normal/normal">{section.designation}</Text>
        </div>
      </Table.Cell>
      <Table.Cell>
        <SubscriptionCoverRow disableIcon={true} shouldBlur={!propDfr}>
          <Text
            variant="text-xs/leading-normal/medium"
            style={{ backgroundColor: getHklColor(section.hkl) }}
            className="rounded-md text-white-50 px-2 py-0-5"
          >
            {section.hkl}
          </Text>
        </SubscriptionCoverRow>
      </Table.Cell>
      <Table.Cell>
        <SubscriptionCoverRow disableIcon={true} className="flex items-center" shouldBlur={!propDfr}>
          <Text variant="text-xs/leading-normal/medium" className="rounded-md border-1 border-[#E6E6E6] px-2 py-0-5">
            {section.si}
          </Text>
        </SubscriptionCoverRow>
      </Table.Cell>
      <Table.Cell>
        <SubscriptionCoverRow disableIcon={true} className="flex items-center" shouldBlur={!propDfr}>
          <Text variant="text-xs/leading-normal/medium" className="rounded-md border-1 border-[#E6E6E6] px-2 py-0-5">
            {getAgeSpan(section.age)}
          </Text>
        </SubscriptionCoverRow>
      </Table.Cell>
      <Table.Cell>
        <SubscriptionCoverRow disableIcon={true} shouldBlur={!propDfr} className="flex items-center">
          <Text variant="text-xs/leading-normal/medium" className="rounded-md border-1 border-[#E6E6E6] px-2 py-0-5">
            {formatNumber(section.area)}
          </Text>
        </SubscriptionCoverRow>
      </Table.Cell>
      <Table.Cell>
        <SubscriptionCoverRow disableIcon={true} className="flex items-center gap-2" shouldBlur={!propDfr}>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-[#D5EE64]" />
            <Text variant="text-xs/leading-normal/normal">T{section.tgl.pine} %</Text>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-[#3F8888]" />
            <Text variant="text-xs/leading-normal/normal">G{section.tgl.fir} %</Text>
          </div>
          <div className="flex items-center gap-1">
            <div className="h-2 w-2 rounded-full bg-[#7C6ED9]" />
            <Text variant="text-xs/leading-normal/normal">L{section.tgl.maple} %</Text>
          </div>
        </SubscriptionCoverRow>
      </Table.Cell>
      <Table.Cell>
        <SubscriptionCoverRow shouldBlur={!propDfr}>
          <Text variant="text-sm/leading-normal/normal">{section.volume.toString()}</Text>
        </SubscriptionCoverRow>
      </Table.Cell>
    </Table.Row>
  );
});

const MobileRow = React.memo(function MobileRow({ section, propDfr, onOpenSection, getHklColor }) {
  return (
    <div
      className="p-3 gap-3 flex flex-col border-b border-gray-200 hover:bg-cloud-200 cursor-pointer"
      onClick={() => onOpenSection(section.nr)}
    >
      <div className="flex justify-between items-center">
        <Text variant="text-xs/leading-normal/normal">
          #{section.avd} · {section.designation}
        </Text>
        <div className="flex flex-row items-baseline gap-0-5 justify-end" style={!propDfr ? { filter: "blur(5px)" } : {}}>
          <Text variant="text-sm/leading-normal/medium" className="text-gray-900 font-medium">
            {section.volume.toString()}
          </Text>
          <Text variant="text-xs/leading-none/normal" as="p">m³sk</Text>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 justify-start">
        <div className="flex flex-row gap-1">
          <div
            style={{ backgroundColor: getHklColor(section.hkl), ...(!propDfr && { filter: "blur(5px)" }) }}
            className="inline-flex items-center px-2 py-0-5 rounded-md"
          >
            <Text variant="text-xs/leading-normal/medium" className="text-white-50">
              {section.hkl}
            </Text>
          </div>
        </div>
        <div className="flex flex-row gap-1">
          <div className="inline-flex items-center px-2 py-0-5 rounded-md border-1 border-[#E6E6E6]" style={!propDfr ? { filter: "blur(5px)" } : {}}>
            <Text variant="text-xs/leading-normal/medium">{section.si}</Text>
          </div>
          <div className="inline-flex items-center px-2 py-0-5 rounded-md border-1 border-[#E6E6E6]" style={!propDfr ? { filter: "blur(5px)" } : {}}>
            <Text variant="text-xs/leading-normal/medium">Ålder {getAgeSpan(section.age)}</Text>
          </div>
          <div className="inline-flex items-center px-2 py-0-5 rounded-md border-1 border-[#E6E6E6]">
            <Text variant="text-xs/leading-normal/medium">Area {section.area}</Text>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-4" style={!propDfr ? { filter: "blur(5px)" } : {}}>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-[#D5EE64]" />
          <Text variant="text-xs/leading-normal/normal" className="text-gray-700">T{section.tgl.pine} %</Text>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-[#3F8888]" />
          <Text variant="text-xs/leading-normal/normal" className="text-gray-700">G{section.tgl.fir} %</Text>
        </div>
        <div className="flex items-center gap-1">
          <div className="h-2 w-2 rounded-full bg-[#7C6ED9]" />
          <Text variant="text-xs/leading-normal/normal" className="text-gray-700">L{section.tgl.maple} %</Text>
        </div>
      </div>
    </div>
  );
});

const tableSkeleton = (length, mobileLength) => {
  return (
    <>
      <div className="overflow-x-auto hidden @md:block h-[385px] @xl:h-[820px] overflow-y-hidden">
        <Table className="w-full">
          <Table.Header>
            <Table.Row className="border-b border-gray-200">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                <Table.Head key={i} className="pb-3">
                  <Skeleton className="h-4 w-14 mb-2" />
                </Table.Head>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {[...Array(length)].map((_, row) => (
              <Table.Row key={row} className="border-b border-gray-100">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((col) => (
                  <Table.Cell key={col} className="py-3 px-2">
                    <Skeleton className="h-4 my-2 w-full" />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
      <div className="block @md:hidden space-y-3 overflow-y-hidden h-[385px]">
        {[...Array(mobileLength)].map((_, row) => (
          <div key={row} className="rounded-lg-alt p-3 gap-3 flex flex-col">
            <div className="flex justify-between items-center">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-6" />
            </div>

            <div className="flex flex-wrap gap-2 justify-start">
              <div className="flex flex-row gap-1">
                <Skeleton className="h-5 w-6 rounded-md" />
                <Skeleton className="h-5 w-6 rounded-md" />
              </div>
              <div className="flex flex-row gap-1">
                <Skeleton className="h-5 w-8 rounded-md" />
                <Skeleton className="h-5 w-14 rounded-md" />
                <Skeleton className="h-5 w-14 rounded-md" />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Skeleton className="h-3 w-7" />
              <Skeleton className="h-3 w-7" />
              <Skeleton className="h-3 w-7" />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const TableWidget = forwardRef(function TableWidget(_props, ref) {
  const { view, setView } = useNavigationStore()
  const { sectionsDetails, loading, propDfr } = useMetricData();

  const [sort, setSort] = useState(DEFAULT_SORT);
  const [sortingShow, setSortingShow] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filter, setFilter] = useState({
    hkl: [],
    age: [0, 100],
    area: [0, 10],
    volume: [0, 5000],
    tgl: []
  });

  const debouncedFilter = useDebounce(filter, FILTER_DEBOUNCE_MS);

  const { maxArea, maxAge, maxVolume, hklOptions } = useMemo(() => {
    const list = sectionsDetails || [];
    if (!list.length) return { maxArea: 10, maxAge: 110, maxVolume: 5000, hklOptions: [] };
    const rawAge = Math.ceil(Math.max(...list.map((s) => getSortValue(s, "age"))) / 10) * 10;

    return {
      maxArea: Math.max(...list.map((s) => getSortValue(s, "area"))),
      maxAge: rawAge > 100 ? 110 : rawAge,
      maxVolume: Math.max(...list.map((s) => getSortValue(s, "volume"))),
      hklOptions: [...new Set(list.map((s) => s.hkl))].filter(Boolean).sort((a, b) => String(a).localeCompare(String(b))),
    };
  }, [sectionsDetails]);

  const handleSort = (key, order=null) => {
    if (order) {
      setSort({ key, order });
    } else {
      const reverseList = ["age", "area", "volume"];

      let defaultOrder = "desc";

      if (reverseList.includes(key)) {
        defaultOrder = "asc";
      }

      setSort((prev) =>
        prev.key === key
          ? { ...prev, order: prev.order === "desc" ? "asc" : "desc" }
          : { key, order: defaultOrder }
      );
    }
  };

  const sections = useMemo(() => {
    const list = sectionsDetails || [];
    const filtered = list.filter((s) => sectionMatchesFilter(s, debouncedFilter));
    if (!SORT_KEYS.includes(sort.key)) return filtered;
    return [...filtered].sort((a, b) => compareSections(a, b, sort.key, sort.order));
  }, [sectionsDetails, debouncedFilter, sort.key, sort.order]);

  useEffect(() => {
    if (loading || !sectionsDetails?.length) return;
    setFilter((prev) => ({
      ...prev,
      area: [0, maxArea],
      age: [0, maxAge],
      volume: [0, maxVolume],
      hkl: [],
      tgl: [],
    }));
  }, [loading, sectionsDetails, maxArea, maxAge, maxVolume]);

  const openSectionInMap = (sectionNumber) => {
    // Because ContentView in MinSkog.js is a scrollable container, we  need to get the scroll position from it.
    /*const mainContainer = document.getElementById("mainContainer");
    const scrollPosition = mainContainer ? mainContainer.scrollTop : 0;

    sessionStorage.setItem("overblickScrollPosition", String(scrollPosition));

    const compObj = {
      comp: "karta",
      section: sectionNumber,
      active: view.active,
    };

    history.pushState(compObj, "");
    setView(compObj);*/
    const newView = { ...view, section: sectionNumber }
    setView(newView);
    history.pushState(newView, "", "");
  };

  const getHklColor = useCallback((hkl) => {
    if (!propDfr) return "#BBBBBB";
    return FellingClassesLegend[hkl]?.color ?? "#BBBBBB";
  }, [propDfr]);

 // Only hide widget when there is no data at all; show empty state when filter has no matches
 if (!loading && (!sectionsDetails || sectionsDetails.length === 0)) {
  return null;
}

const isSortingDefault = (sort.key == DEFAULT_SORT.key && sort.order == DEFAULT_SORT.order)
const filterAmount = (filter.hkl.length != 0) + (filter.age[0] != 0 || filter.age[1] != maxAge) + (filter.area[0] != 0 || filter.area[1] != maxArea) + (filter.volume[0] != 0 || filter.volume[1] != maxVolume) + (filter.tgl.length != 0)

return <WidgetWrapper
  ref={ref}
  title="Avdelningsbeskrivning"
  subTitle={!loading && `${sections?.length} / ${sectionsDetails?.length}`}
  className="col-span-1 @md:col-span-2 @xl:col-span-3 row-span-1 @xl:row-span-2"
  extraContent={(!loading &&<>
    <div className='flex h-full ms-auto gap-2'>
      <Button size='small' variant='outline' onClick={() => setShowFilterModal(true)}><FilterIcon width='20px' />
        <span className='hidden @md:inline'>Filtrera</span> 
        <div className={`${filterAmount == 0 ? 'hidden' : 'flex items-center justify-center'} text-white-50 bg-[#0F0F0F] rounded-full p-1 w-[22px] h-[22px]`}>
          <Text variant="text-xs/leading-normal/semibold">{filterAmount}</Text>
        </div>
      </Button>
      <Button className='@md:hidden' size='small' variant={`${isSortingDefault ? 'outline' : 'primary'}`} onClick={() => setSortingShow('pulpwood')}><SortingArrowsIcon width='20px' fill={`${isSortingDefault ? '#000000' : '#FFFFFF'}`} /></Button>
    </div>
  </>)}
  infoContent={
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <Text variant="text-sm/leading-normal/normal">
          Avdelningsbeskrivningen innehåller detaljerad information
          om varje enskilt bestånd (eller avdelning) inom
          fastigheten. Varje avdelning är ett avgränsat område med
          likartade förutsättningar, till exempel trädslag, ålder,
          bonitet och skötseltillstånd.
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          I beskrivningen anges:
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          <b>Areal:</b> avdelningens storlek i hektar.
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          <b>Trädslag och ålder:</b> vilka träd som dominerar och
          hur gammal skogen är.
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          <b>Virkesförråd och tillväxt:</b> mängden stående virke
          och hur mycket den växer per år.
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          <b>Huggningsklass:</b> vilket utvecklingsstadium skogen
          befinner sig i (t.ex. ungskog, gallringsskog,
          slutavverkningsskog).
        </Text>
      </div>
      <div className="flex flex-col gap-2">
        <Text variant="text-sm/leading-normal/medium">
          Syfte och användning
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          Avdelningsbeskrivningen ger en praktisk och uppdaterad
          bild av skogen på beståndsnivå. Den används för att:
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          Planera skötselåtgärder och avverkningar i tid och rum.
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          Beräkna virkesförråd, tillväxt och ekonomiskt värde.
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          Skapa underlag för hållbart och långsiktigt brukande av
          skogen.
        </Text>
        <Text variant="text-sm/leading-normal/normal">
          Tillsammans med kartan utgör avdelningsbeskrivningen den
          operativa delen av skogsbruksplanen, där övergripande
          strategier omsätts i konkreta åtgärder för varje del av
          fastigheten.
        </Text>
      </div>
    </div>
  }
>
  {loading ? tableSkeleton(14, 3) : <>
    {/* Desktop Table */}
    <div className="hidden @md:block">
      <Table wrapperClassName="overflow-y-auto h-[360px] @xl:h-[820px]">
        <Table.Header className="sticky top-0 bg-cloud-200">
          <Table.Row>
            <Table.Head>
              <button
                type="button"
                onClick={() => handleSort("avd")}
                className={`text-left w-full font-medium flex items-center gap-0.5 ${sort.key === "avd" ? "text-gray-900" : "text-gray-600 hover:text-gray-900"} cursor-pointer`}
              >
                <Text variant="text-sm/leading-normal/medium">Avd.</Text>
                {sort.key === "avd" && (
                  <CaretClosedDown width={16} height={16} fill="000" className={`${sort.order === "desc" ? "" : "rotate-180"}`} />
                )}
              </button>
            </Table.Head>
            <Table.Head>
              <button
                type="button"
                onClick={() => handleSort("designation")}
                className={`text-left w-full font-medium flex items-center gap-0.5 ${sort.key === "designation" ? "text-gray-900" : "text-gray-600 hover:text-gray-900"} cursor-pointer`}
              >
                <Text variant="text-sm/leading-normal/medium">Fastighet</Text>
                {sort.key === "designation" && (
                  <CaretClosedDown width={16} height={16} fill="000" className={`${sort.order === "desc" ? "" : "rotate-180"}`} />
                )}
              </button>
            </Table.Head>
            <Table.Head>
              <button
                type="button"
                onClick={() => handleSort("hkl")}
                className={`text-left w-full font-medium flex items-center gap-0.5 ${sort.key === "hkl" ? "text-gray-900" : "text-gray-600 hover:text-gray-900"} cursor-pointer`}
              >
                <Text variant="text-sm/leading-normal/medium">HKL</Text>
                {sort.key === "hkl" && (
                  <CaretClosedDown width={16} height={16} fill="000" className={`${sort.order === "desc" ? "" : "rotate-180"}`} />
                )}
              </button>
            </Table.Head>
            {/* <Table.Head>
              <Text variant="text-sm/leading-normal/medium">Bonitet</Text>
            </Table.Head> */}
            <Table.Head>
              <button
                type="button"
                onClick={() => handleSort("si")}
                className={`text-left w-full font-medium flex items-center gap-0.5 ${sort.key === "si" ? "text-gray-900" : "text-gray-600 hover:text-gray-900"} cursor-pointer`}
              >
                <Text variant="text-sm/leading-normal/medium">SI</Text>
                {sort.key === "si" && (
                  <CaretClosedDown width={16} height={16} fill="000" className={`${sort.order === "desc" ? "" : "rotate-180"}`} />
                )}
              </button>
            </Table.Head>
            <Table.Head>
              <button
                type="button"
                onClick={() => handleSort("age")}
                className={`text-left w-full font-medium flex items-center gap-0.5 ${sort.key === "age" ? "text-gray-900" : "text-gray-600 hover:text-gray-900"} cursor-pointer`}
              >
                <Text variant="text-sm/leading-normal/medium">Ålder</Text>
                {sort.key === "age" && (
                  <CaretClosedDown width={16} height={16} fill="000" className={`${sort.order === "desc" ? "" : "rotate-180"}`} />
                )}
              </button>
            </Table.Head>
            <Table.Head>
              <button
                type="button"
                onClick={() => handleSort("area")}
                className={`text-left w-full font-medium flex items-center gap-0.5 ${sort.key === "area" ? "text-gray-900" : "text-gray-600 hover:text-gray-900"} cursor-pointer`}
              >
                <Text variant="text-sm/leading-normal/medium">Areal (ha)</Text>
                {sort.key === "area" && (
                  <CaretClosedDown width={16} height={16} fill="000" className={`${sort.order === "desc" ? "" : "rotate-180"}`} />
                )}
              </button>
            </Table.Head>
            <Table.Head>
              <Text variant="text-sm/leading-normal/medium">T | G | L</Text>
            </Table.Head>
            <Table.Head>
              <button
                type="button"
                onClick={() => handleSort("volume")}
                className={`text-left w-full font-medium flex items-center gap-0.5 ${sort.key === "volume" ? "text-gray-900" : "text-gray-600 hover:text-gray-900"} cursor-pointer`}
              >
                <Text variant="text-sm/leading-normal/medium">
                  Volym (m³sk)
                </Text>
                {sort.key === "volume" && (
                  <CaretClosedDown width={16} height={16} fill="000" className={`${sort.order === "desc" ? "" : "rotate-180"}`} />
                )}
              </button>
            </Table.Head>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {sections.map((section) => (
            <TableRow
              key={section.nr ?? section.avd}
              section={section}
              propDfr={propDfr}
              onOpenSection={openSectionInMap}
              getHklColor={getHklColor}
              view={view}
            />
          ))}
        </Table.Body>
      </Table>
    </div>

    {/* Mobile Tablet */}
    <div className="@md:hidden overflow-y-auto h-[360px]">
      {sections.map((section) => (
        <MobileRow
          key={section.nr ?? section.avd}
          section={section}
          propDfr={propDfr}
          onOpenSection={openSectionInMap}
          getHklColor={getHklColor}
        />
      ))}

      <Drawer isOpen={sortingShow} onClose={() => setSortingShow(false)}>
        <Drawer.Header className="pb-2-5 justify-between items-center">
          <div className="flex w-full items-center justify-between">
            <Text variant="text-sm/leading-normal/medium">Sortera</Text>
            <Button variant="tertiary" size="icon" onClick={() => setSortingShow(false)} aria-label="Stäng">
              <CrossIcon width='20px' />
            </Button>
          </div>
        </Drawer.Header>
        <Drawer.Content>
          <div className="flex flex-col gap-2">
            <Button variant={(sort.key == 'avd' && sort.order == 'desc') ? 'primary' : 'outline'} onClick={() => handleSort("avd", "desc")} className="justify-start">Avdelning (Stigande)</Button>
            <Button variant={(sort.key == 'avd' && sort.order == 'asc') ? 'primary' : 'outline'} onClick={() => handleSort("avd", "asc")} className="justify-start">Avdelning (Fallande)</Button>
            <Button variant={(sort.key == 'designation' && sort.order == 'desc') ? 'primary' : 'outline'} onClick={() => handleSort("designation", "desc")} className="justify-start">Fastighet (Namn A-Ö)</Button>
            <Button variant={(sort.key == 'designation' && sort.order == 'asc') ? 'primary' : 'outline'} onClick={() => handleSort("designation", "asc")} className="justify-start">Fastighet (Namn Ö-A)</Button>
            <Button variant={(sort.key == 'hkl' && sort.order == 'desc') ? 'primary' : 'outline'} onClick={() => handleSort("hkl", "desc")} className="justify-start">Huggningsklass (Namn A-Ö)</Button>
            <Button variant={(sort.key == 'hkl' && sort.order == 'asc') ? 'primary' : 'outline'} onClick={() => handleSort("hkl", "asc")} className="justify-start">Huggningsklass (Namn Ö-A)</Button>
            <Button variant={(sort.key == 'si' && sort.order == 'desc') ? 'primary' : 'outline'} onClick={() => handleSort("si", "desc")} className="justify-start">SI (Högsta)</Button>
            <Button variant={(sort.key == 'si' && sort.order == 'asc') ? 'primary' : 'outline'} onClick={() => handleSort("si", "asc")} className="justify-start">SI (Lägsta)</Button>
            <Button variant={(sort.key == 'age' && sort.order == 'asc') ? 'primary' : 'outline'} onClick={() => handleSort("age", "asc")}  className="justify-start">Ålder (Högsta)</Button>
            <Button variant={(sort.key == 'age' && sort.order == 'desc') ? 'primary' : 'outline'} onClick={() => handleSort("age", "desc")} className="justify-start">Ålder (Lägsta)</Button>
            <Button variant={(sort.key == 'area' && sort.order == 'asc') ? 'primary' : 'outline'} onClick={() => handleSort("area", "asc")} className="justify-start">Areal (Högsta)</Button>
            <Button variant={(sort.key == 'area' && sort.order == 'desc') ? 'primary' : 'outline'} onClick={() => handleSort("area", "desc")} className="justify-start">Areal (Lägsta)</Button>
            <Button variant={(sort.key == 'volume' && sort.order == 'asc') ? 'primary' : 'outline'} onClick={() => handleSort("volume", "asc")} className="justify-start">Volym (Högsta)</Button>
            <Button variant={(sort.key == 'volume' && sort.order == 'desc') ? 'primary' : 'outline'} onClick={() => handleSort("volume", "desc")} className="justify-start">Volym (Lägsta)</Button>
          </div>
        </Drawer.Content>
        <Drawer.Footer>
          <Button variant="secondary" className='w-full mb-2' onClick={() => setSort(DEFAULT_SORT)}>Återställ</Button>
          <Button className='w-full' onClick={() => setSortingShow(false)}>Stäng</Button>
        </Drawer.Footer>
      </Drawer>
    </div>

    <DynamicModal isOpen={showFilterModal} onClose={() => setShowFilterModal(false)} bp={{ sm: "drawer", lg: "sheet" }} >
      <DynamicModal.Header>
        <div className="w-full text-center">
          <Text variant="text-sm/leading-normal/medium">Filtrera avdelningar</Text>
        </div>
      </DynamicModal.Header>

      <DynamicModal.Content>
        <div className='flex flex-col overflow-hidden h-full'>
          <div className='flex-1 overflow-y-auto mt-2' style={{ scrollbarWidth: 'none' }}>
            <Accordion isOpen={true} className="border-b border-solid border-[#E6E6E6] py-4">
              <Accordion.Header
                action={(filter.hkl.length > 0) && 
                  <Text
                    variant="text-xs/leading-normal/medium"
                    onClick={() => setFilter({ ...filter, hkl: [] })}
                    className="cursor-pointer me-2"
                  >
                    Rensa
                  </Text>
                }
              >
                <Text variant="text-sm/leading-normal/medium">Huggningsklasser</Text>
              </Accordion.Header>
              <Accordion.Content className="pt-4">
                <div className="grid grid-cols-2 auto-rows-auto gap-2">
                  <div key="Alla" className={`flex items-center justify-center cursor-pointer rounded-[10px] h-[48px] border-1 ${ filter.hkl.length === 0 ? 'bg-[#0F0F0F] text-[#FBFBFB] border-[#0F0F0F]' : 'bg-[#F5F5F5] text-[#0F0F0F] border-[#E6E6E6]' }`} onClick={() => setFilter({ ...filter, hkl: [] })}>
                    <Text variant="text-sm/leading-normal/medium">Alla</Text>
                  </div>
                  
                  {hklOptions?.map((hkl) => {
                    return (
                      <div key={hkl} className={`flex items-center justify-center cursor-pointer rounded-[10px] h-[48px] border-1 ${ filter.hkl.includes(hkl) ? 'bg-[#0F0F0F] text-[#FBFBFB] border-[#0F0F0F]' : 'bg-[#F5F5F5] text-[#0F0F0F] border-[#E6E6E6]' }`} onClick={() => {
                        const newHkl = filter.hkl.includes(hkl) ? filter.hkl.filter((h) => h !== hkl) : [...filter.hkl, hkl];
                        const allHkl = hklOptions.every((t) => newHkl.includes(t));
                        setFilter({ ...filter, hkl: allHkl ? [] : newHkl });
                      }}>
                        <Text variant="text-sm/leading-normal/medium">{hkl}</Text>
                      </div>
                    )
                  })}
                </div>
              </Accordion.Content>
            </Accordion>

            <Accordion isOpen={true} className="border-b border-solid border-[#E6E6E6] py-4">
              <Accordion.Header
                action={(filter.tgl.length > 0) && 
                  <Text
                    variant="text-xs/leading-normal/medium"
                    onClick={() => setFilter({ ...filter, tgl: [] })}
                    className="cursor-pointer me-2"
                  >
                    Rensa
                  </Text>
                }
              >
                <Text variant="text-sm/leading-normal/medium">Trädslag</Text>
              </Accordion.Header>
              <Accordion.Content className="pt-4">
                <div className="grid grid-cols-2 auto-rows-auto gap-2">
                  <div key="Alla" className={`flex items-center justify-center cursor-pointer rounded-[10px] h-[48px] border-1 ${ filter.tgl.length === 0 ? 'bg-[#0F0F0F] text-[#FBFBFB] border-[#0F0F0F]' : 'bg-[#F5F5F5] text-[#0F0F0F] border-[#E6E6E6]' }`} onClick={() => setFilter({ ...filter, tgl: [] })}>
                    <Text variant="text-sm/leading-normal/medium">Alla</Text>
                  </div>

                  {Object.entries({fir: "Gran", pine: "Tall", maple: "Löv"}).map(([key, value]) => {
                    return (
                      <div
                        key={key}
                        className={`flex items-center justify-center cursor-pointer rounded-[10px] h-[48px] border-1 ${ filter.tgl.includes(key) ? 'bg-[#0F0F0F] text-[#FBFBFB] border-[#0F0F0F]' : 'bg-[#F5F5F5] text-[#0F0F0F] border-[#E6E6E6]' }`}
                        onClick={() => {
                          const newTgl = filter.tgl.includes(key) ? filter.tgl.filter((h) => h !== key) : [...filter.tgl, key];
                          const allThree = ["fir", "pine", "maple"].every((t) => newTgl.includes(t));
                          setFilter({ ...filter, tgl: allThree ? [] : newTgl });
                        }}
                      >
                        <Text variant="text-sm/leading-normal/medium">{value}</Text>
                      </div>
                    )
                  })}
                </div>
              </Accordion.Content>
            </Accordion>

            <Accordion isOpen={true} className="border-b border-solid border-[#E6E6E6] py-4">
              <Accordion.Header
                action={(filter.volume[0] !== 0 || filter.volume[1] !== maxVolume) && 
                  <Text
                    variant="text-xs/leading-normal/medium"
                    onClick={() => setFilter({ ...filter, volume: [0, maxVolume] })}
                    className="cursor-pointer me-2"
                  >
                    Rensa
                  </Text>
                }>
                <Text variant="text-sm/leading-normal/medium">Volym</Text>
              </Accordion.Header>
              <Accordion.Content className="pt-4">
                {!loading && <>
                  <div className="flex gap-2">
                    <Input variant="fluid" type="number" step={1} min={0} max={maxVolume} value={filter.volume[0]} onChange={(val) => setFilter({ ...filter, volume: [parseFilterBound(val, 0), filter.volume[1]] })} />
                    <Input variant="fluid" type="number" step={1} min={0} max={maxVolume} value={filter.volume[1]} onChange={(val) => setFilter({ ...filter, volume: [filter.volume[0], parseFilterBound(val, maxVolume)] })} />
                  </div>
                  <SliderBar
                    className="w-full my-4"
                    value={[filter.volume[0], filter.volume[1]]}
                    step={1}
                    min={0}
                    max={maxVolume}
                    onValueChange={(val) => setFilter({ ...filter, volume: [parseFilterBound(val[0], 0), parseFilterBound(val[1], maxVolume)] })}
                  />
                </>}
              </Accordion.Content>
            </Accordion>

            <Accordion isOpen={true} className="border-b border-solid border-[#E6E6E6] py-4">
              <Accordion.Header
                action={(filter.area[0] !== 0 || filter.area[1] !== maxArea) && <Text
                    variant="text-xs/leading-normal/medium"
                    onClick={() => setFilter({ ...filter, area: [0, maxArea] })}
                    className="cursor-pointer me-2"
                  >
                    Rensa
                  </Text>
                }>
                <Text variant="text-sm/leading-normal/medium">Areal</Text>
              </Accordion.Header>
              <Accordion.Content className="pt-4">
                {!loading && <>
                  <div className="flex gap-2">
                    <Input variant="fluid" type="number" step={0.1} min={0} max={maxArea} value={filter.area[0]} onChange={(val) => setFilter({ ...filter, area: [parseFilterBound(val, 0), filter.area[1]] })} />
                    <Input variant="fluid" type="number" step={0.1} min={0} max={maxArea} value={filter.area[1]} onChange={(val) => setFilter({ ...filter, area: [filter.area[0], parseFilterBound(val, maxArea)] })} />
                  </div>
                  <SliderBar
                    className="w-full my-4"
                    value={[filter.area[0], filter.area[1]]}
                    step={0.1}
                    min={0}
                    max={maxArea}
                    onValueChange={(val) => setFilter({ ...filter, area: [parseFilterBound(val[0], 0), parseFilterBound(val[1], maxArea)] })}
                  />
                </>}
              </Accordion.Content>
            </Accordion>

            <Accordion isOpen={true} className="border-b border-solid border-[#E6E6E6] py-4">
              <Accordion.Header
                action={(filter.age[0] !== 0 || filter.age[1] !== maxAge) && 
                  <Text
                    variant="text-xs/leading-normal/medium"
                    onClick={() => setFilter({ ...filter, age: [0, maxAge] })}
                    className="cursor-pointer me-2"
                  >
                    Rensa
                  </Text>
                }>
                <Text variant="text-sm/leading-normal/medium">Ålder</Text>
              </Accordion.Header>
              <Accordion.Content className="pt-4">
                {!loading && <>
                  <div className="flex gap-2">
                    <Input variant="fluid" type="number" step={10} min={0} max={maxAge} value={filter.age[0]} onChange={(val) => setFilter({ ...filter, age: [parseFilterBound(val, 0), filter.age[1]] })} />
                    <Input
                      variant="fluid"
                      type={filter.age[1] > 100 ? "text" : "number"}
                      step={10}
                      min={0}
                      max={maxAge}
                      value={filter.age[1] > 100 ? "100+" : filter.age[1]}
                      onChange={(val) => {
                        const num = val === "100+" ? maxAge : parseFilterBound(val, maxAge);
                        setFilter({ ...filter, age: [filter.age[0], num] });
                      }}
                    />
                  </div>
                  <SliderBar
                    className="w-full my-4"
                    value={[filter.age[0], filter.age[1]]}
                    step={10}
                    min={0}
                    max={maxAge}
                    onValueChange={(val) => setFilter({ ...filter, age: [parseFilterBound(val[0], 0), parseFilterBound(val[1], maxAge)] })}
                  />
                </>}
              </Accordion.Content>
            </Accordion>
          </div>
        </div>
      </DynamicModal.Content>

      <DynamicModal.Footer>
        <Button className="mb-2" variant="secondary" fullWidth onClick={() => setFilter({ ...filter, hkl: [], age: [0, maxAge], area: [0, maxArea], volume: [0, maxVolume], tgl: [] })}>Rensa allt</Button>
        <Button variant="primary" fullWidth onClick={() => setShowFilterModal(false)}>{filterAmount > 0 ? 'Visa' : 'Stäng'}</Button>
      </DynamicModal.Footer>
    </DynamicModal>
  </>}
</WidgetWrapper>
});

export default TableWidget;
